import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { ago } from "../lib/markdown";
import type { Changelog } from "../types";

// Top-bar pill, next to the "Omni Hub" wordmark text, showing the current
// version (the most recently merged PR number — trunk is squash-merge-only,
// so it increments roughly 1:1 with what ships). Click opens a popover with a
// plain-English changelog (each entry versioned + PR-linked) and, at the
// bottom, a link to the deploy workflow. Mirrors CliInstallButton's pill +
// portal-popover pattern.
export default function VersionBadge() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Changelog | null>(null);
  const [error, setError] = useState(false);
  const pillRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 64, left: 24 });

  useEffect(() => {
    api.getChangelog().then(setData).catch(() => setError(true));
  }, []);

  function toggleOpen() {
    if (!open && pillRef.current) {
      const r = pillRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 8, left: r.left });
    }
    setOpen((o) => !o);
  }

  // Close on Escape for keyboard users (backdrop click handles pointer dismissal).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (error) return null;

  return (
    <div className="presence">
      <button
        ref={pillRef}
        className="presence-pill version-badge"
        onClick={toggleOpen}
        title="What is new in Omni Hub"
        aria-expanded={open}
      >
        {data ? data.version : "…"}
      </button>

      {open && createPortal(
        <>
          <div className="presence-backdrop" onClick={() => setOpen(false)} />
          <div className="version-pop" role="dialog" aria-label="Omni Hub version" style={{ top: coords.top, left: coords.left }}>
            {!data ? (
              <div className="version-pop-loading">Loading…</div>
            ) : (
              <>
                {data.changes.length > 0 && (
                  <div className="version-pop-section">
                    <div className="version-pop-label">Recent changes</div>
                    <ul className="version-pop-list">
                      {data.changes.map((c) => (
                        <li key={c.number}>
                          <div className="version-pop-meta">v{c.number} · {ago(c.mergedAt)}</div>
                          <a href={c.url} target="_blank" rel="noreferrer">#{c.number} {c.sentence}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <a className="version-pop-deploy" href={data.deployUrl} target="_blank" rel="noreferrer">
                  View deployment history ↗
                </a>
              </>
            )}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
